// Library methods
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import TodayExamCard from "./TodayExamCard/TodayExamCard";
import ExamSlider from "../../shared/ExamSlider/ExamSlider";
import SelectPatientModal from "./SelectPatientModal/SelectPatientModal";
import LiveUpdateModal from "../../shared/LiveUpdateModal/LiveUpdateModal";

// Utils
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { getUtcStartEndDate } from "src/utils/date/dateHelper";
import {
  getAllExamSections,
  getExamCardText,
  getReportEyeText,
  isMultiExamPackage,
} from "src/utils/exams/examReportsHelper";
import { getTruncatedText } from "src/utils/text/textHelper";

// Types
import { OrderType } from "src/types/sharedTypes";
import { ExamType } from "src/services/exams/types";

// Hooks
import { useLinkedExamsData } from "src/services/exams/hooks/linked-exams/useLinkedExamsData";
import { useScheduledExamsData } from "src/services/exams/hooks/dashboard-exams/useScheduledExamsData";
import { useRecentlyCompletedExamsData } from "src/services/exams/hooks/dashboard-exams/useRecentlyCompletedExamsData";
import { useExamStatus } from "src/hooks/exams/useExamStatus";
import useOnlineConnection from "src/hooks/connection/useOnlineConnection";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Styles
import { useTheme } from "@mui/material/styles";

const LOADING_NUM_OF_ROWS = 5;
const MAX_NUM_OF_ROWS = 10;

type ExtendedExamType = ExamType & {
  firstName: string;
  lastName: string;
  examType: string;
  fullExamType: string;
  examStatus: string;
  eyesOrder: string;
};

export type TodayExamsHandleType = {
  openAddExamModal: () => void;
};

type TodayExamsType = {
  timeRange: number;
  // Reports isLoading up to TodayAccordion, which renders the "Add Exam"
  // button (and its loading state) itself now.
  onLoadingChange?: (isLoading: boolean) => void;
  searchTerm?: string;
  forceExpandKey?: number;
};

const TodayExams = forwardRef<TodayExamsHandleType, TodayExamsType>(
  ({ timeRange, onLoadingChange, searchTerm = "", forceExpandKey }, ref) => {
    const { t } = useTranslation();
    const { clinicInfo } = useClinicContext();

    const theme = useTheme();

    const { open } = useLiveUpdateContext();
    const { devices, setDevices } = useDevicesContext();
    const { getExamStatus } = useExamStatus();
    const { actions } = useExamSliderContext();
    const {
      addExamWrapper,
      editExamWrapper,
      handleInitializeEdit,
      resetValues,
    } = actions;

    const todayAndLinkedExamsFilterPayload = useMemo(() => {
      const todayUtc = getUtcStartEndDate(0, clinicInfo?.timezone);
      return {
        startDate: todayUtc.startOfUTC,
        endDate: todayUtc.endOfUTC,
      };
    }, [clinicInfo?.timezone]);

    const recentExamFilterPayload = useMemo(
      () => ({
        recentFilterDate: getUtcStartEndDate(timeRange, clinicInfo?.timezone)
          .startOfUTC,
      }),
      [timeRange, clinicInfo?.timezone]
    );

    const {
      data: todayExamsData,
      isLoading: isLoadingTodayExams,
      isError: isErrorTodayExams,
      refetch: refetchTodayExams,
    } = useScheduledExamsData(clinicInfo?.id, todayAndLinkedExamsFilterPayload);

    const {
      data: linkedExamsData,
      isLoading: isLoadingLinkedExams,
      isError: isErrorLinkedExams,
      refetch: refetchLinkedExams,
    } = useLinkedExamsData(clinicInfo?.id, todayAndLinkedExamsFilterPayload);

    const { refetch: refetchRecentExams } = useRecentlyCompletedExamsData(
      clinicInfo?.id,
      recentExamFilterPayload
    );

    const refetchExamsWrapper = useCallback(async () => {
      await refetchTodayExams();
      await refetchLinkedExams();
      await refetchRecentExams();
    }, [refetchTodayExams, refetchLinkedExams, refetchRecentExams]);

    const [isLoadingSlider, setIsLoadingSlider] = useState(false);

    const formattedExams: Array<ExtendedExamType> = useMemo(() => {
      const linkedExamsToInclude = linkedExamsData?.filter(
        (linkedExam) =>
          !todayExamsData?.find((todayExam) => todayExam.id === linkedExam.id)
      );
      const allExams = [
        ...(todayExamsData ?? []),
        ...(linkedExamsToInclude ?? []),
      ];
      return allExams?.map((exam) => {
        const device = devices?.find(
          (item) => item?.associatedExam?.exam?.id === exam?.id
        );

        return {
          ...exam,
          firstName: exam.patient.firstName,
          lastName: exam.patient.lastName,
          examType: getExamCardText({ exam, t }),
          fullExamType: getExamCardText({ exam, t, isAbbrev: false }),
          examStatus:
            device || exam
              ? getExamStatus({ device, exam })?.status ?? ""
              : t("Not Started"),
          // Matches Exams.tsx's examEyeOrder -- translated (e.g. "Both" ->
          // "Binocular") so searching matches what's actually displayed.
          eyesOrder: isMultiExamPackage(exam)
            ? ""
            : getAllExamSections(exam)
                .map((section) => getReportEyeText(section.eye, t))
                .join(" "),
        };
      });
    }, [t, linkedExamsData, todayExamsData, devices, getExamStatus]);

    const isLoading = useMemo(
      () => isLoadingSlider || isLoadingTodayExams || isLoadingLinkedExams,
      [isLoadingSlider, isLoadingTodayExams, isLoadingLinkedExams]
    );

    useEffect(() => {
      onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    useEffect(() => {
      if (forceExpandKey) setExpanded(true);
    }, [forceExpandKey]);

    const hasExams = useMemo(
      () =>
        !isLoading &&
        !isErrorTodayExams &&
        !isErrorLinkedExams &&
        formattedExams &&
        formattedExams?.length > 0,
      [isLoading, isErrorTodayExams, isErrorLinkedExams, formattedExams]
    );

    const hasNoExams = useMemo(
      () =>
        !isLoading &&
        (isErrorTodayExams || isErrorLinkedExams || !formattedExams?.length),
      [isLoading, isErrorTodayExams, isErrorLinkedExams, formattedExams]
    );

    // Filtered exams
    const filteredExams: Array<ExtendedExamType> = useMemo(() => {
      const lowerSearchTerm = searchTerm?.toLowerCase();
      if (!lowerSearchTerm) return formattedExams;

      return formattedExams?.filter((exam) => {
        const searchTermHasName = `${exam?.firstName} ${exam?.lastName}`
          ?.toLowerCase()
          ?.includes(lowerSearchTerm);

        const searchTermHasStatus = exam?.examStatus
          ?.toString()
          ?.toLowerCase()
          ?.includes(lowerSearchTerm);

        const searchTermHasEye = exam?.eyesOrder
          ?.toString()
          ?.toLowerCase()
          ?.includes(lowerSearchTerm);

        const searchTermHasAlgorithm = exam?.examType
          ?.toString()
          ?.toLowerCase()
          ?.includes(lowerSearchTerm);

        const searchTermHasFullAlgorithm = exam?.fullExamType
          ?.toString()
          ?.toLowerCase()
          ?.includes(lowerSearchTerm);

        return (
          searchTermHasName ||
          searchTermHasStatus ||
          searchTermHasEye ||
          searchTermHasAlgorithm ||
          searchTermHasFullAlgorithm
        );
      });
    }, [formattedExams, searchTerm]);

    const hasNoSearchResults = useMemo(
      () => searchTerm?.length > 0 && !filteredExams?.length,
      [searchTerm, filteredExams]
    );

    const [openPatientModal, setOpenPatientModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<{
      displayName: string;
      value: string;
    }>({ displayName: "", value: "" });
    const [openAddExamSlider, setOpenAddExamSlider] = useState(false);
    const [openEditExamSlider, setOpenEditExamSlider] = useState(false);

    // Lets TodayAccordion's own "Add Exam" button trigger this component's
    // patient-select modal without lifting all of the slider/modal state up.
    useImperativeHandle(ref, () => ({
      openAddExamModal: () => {
        resetValues();
        setSelectedPatient({ displayName: "", value: "" });
        setOpenPatientModal(true);
      },
    }));

    const orderByPatientName: Array<keyof ExtendedExamType> = [
      "firstName",
      "lastName",
    ];
    const orderByExamStatus: Array<keyof ExtendedExamType> = ["examStatus"];
    const orderByExamType: Array<keyof ExtendedExamType> = ["examType"];
    const orderByEyesOrder: Array<keyof ExtendedExamType> = ["eyesOrder"];

    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState(true);
    const [order, setOrder] = useState<OrderType>("asc");

    const [orderByOptions, setOrderByOptions] = useState<
      Array<keyof ExtendedExamType>
    >([...orderByPatientName]);

    const headCells = [
      {
        id: "patient",
        label: t("Patient"),
        gridSize: 3,
        orderUsing: [...orderByPatientName, ...orderByExamType],
      },
      {
        id: "status",
        label: t("Status"),
        gridSize: 3,
        orderUsing: [
          ...orderByExamStatus,
          ...orderByPatientName,
          ...orderByExamType,
        ],
      },
      {
        id: "eye",
        label: t("Eye"),
        gridSize: 3,
        orderUsing: [
          ...orderByEyesOrder,
          ...orderByPatientName,
          ...orderByExamType,
        ],
      },
      {
        id: "type",
        label: t("Type"),
        gridSize: 3,
        orderUsing: [...orderByExamType, ...orderByPatientName],
      },
    ];

    const handleRequestSort = useCallback(
      (
        _: React.MouseEvent<HTMLButtonElement>,
        property: Array<keyof ExtendedExamType>
      ) => {
        const isAsc =
          orderByOptions.length &&
          orderByOptions[0] === property[0] &&
          order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderByOptions(property);
      },
      [order, orderByOptions]
    );

    const sortedExams: Array<ExtendedExamType> = useMemo(() => {
      if (!filteredExams?.length) return [];

      const notStartedDeviceExams: Array<ExtendedExamType> = [];
      const notLiveExams: Array<ExtendedExamType> = [];
      const liveExams: Array<ExtendedExamType> = filteredExams.filter((row) => {
        const device = devices?.find(
          (item) => item?.associatedExam?.exam?.id === row?.id
        );

        if (
          device &&
          device?.deviceStatus?.stage?.stageType &&
          device?.deviceStatus?.stage?.stageType !== t("Not Started")
        ) {
          return true;
        }

        if (device) {
          notStartedDeviceExams.push(row);
          return false;
        } else {
          notLiveExams.push(row);
          return false;
        }
      });

      const sortedLiveExams = multiKeyCompare<ExtendedExamType>(
        liveExams,
        order === "asc",
        orderByOptions
      );

      const sortedNotStartedDeviceExams = multiKeyCompare<ExtendedExamType>(
        notStartedDeviceExams,
        order === "asc",
        orderByOptions
      );

      const sortedNotLiveExams = multiKeyCompare<ExtendedExamType>(
        notLiveExams,
        order === "asc",
        orderByOptions
      );

      return [
        ...sortedLiveExams,
        ...sortedNotStartedDeviceExams,
        ...sortedNotLiveExams,
      ];
    }, [t, devices, filteredExams, order, orderByOptions]);

    // exams to render
    const examsOnCurrentPage = useMemo(() => {
      return getItemsToRender(sortedExams, page, MAX_NUM_OF_ROWS);
    }, [sortedExams, page]);

    // Pagination change
    const onPaginationChange = useCallback(
      (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
      },
      [setPage]
    );

    useEffect(() => setPage(1), [sortedExams]);

    // handle adding a new exam, refetching data, and displaying a toast
    const onConfirmAddExam = useCallback(async () => {
      setIsLoadingSlider(true);

      try {
        await addExamWrapper(selectedPatient.value);
        await refetchLinkedExams();
        await refetchTodayExams();

        setOpenAddExamSlider(false);
      } catch (error) {
        // do nothing
      } finally {
        setIsLoadingSlider(false);
      }
    }, [
      addExamWrapper,
      refetchLinkedExams,
      refetchTodayExams,
      selectedPatient,
    ]);

    // handle opening the edit modal from the exam card
    const handleEditClick = useCallback(
      (editExam: ExamType) => {
        const patientName = getTruncatedText(
          `${editExam?.patient?.firstName} ${editExam?.patient?.lastName}`
        );
        const birthdate = editExam?.patient?.dateOfBirth?.slice(0, 10) ?? "N/A";
        setSelectedPatient({
          displayName: `${patientName} / ${birthdate}`,
          value: editExam?.patient?.id,
        });

        resetValues();
        handleInitializeEdit(editExam);
        setOpenEditExamSlider(true);
      },
      [handleInitializeEdit, resetValues]
    );

    // handle updating an exam, refetching data, and displaying a toast
    const onConfirmEditExam = useCallback(async () => {
      setIsLoadingSlider(true);

      try {
        await editExamWrapper(selectedPatient.value);
        await refetchLinkedExams();
        await refetchTodayExams();

        setOpenEditExamSlider(false);
      } catch (error) {
        // do nothing
      } finally {
        setIsLoadingSlider(false);
      }
    }, [
      editExamWrapper,
      refetchLinkedExams,
      refetchTodayExams,
      selectedPatient,
    ]);

    // Handle updating the device array based on signalR data
    const handleDeviceListUpdated = useCallback(
      (data: string) => {
        const parsedData = JSON.parse(data);
        setDevices([...parsedData?.data]);
      },
      [setDevices]
    );

    // signalR setup + online status
    const { connection, isLoadingSignalR } = useOnlineConnection({
      clinicId: clinicInfo?.id,
      handleDeviceListUpdated,
    });

    useEffect(() => {
      if (!open) {
        connection?.off("DeviceExamSubmitted");
        connection?.on("DeviceExamSubmitted", function () {
          refetchExamsWrapper();
        });
      }
    }, [open, connection, refetchExamsWrapper]);

    useEffect(() => {
      connection?.off("ClinicLensesUpdated");
      connection?.on("ClinicLensesUpdated", function () {
        refetchExamsWrapper();
      });
    }, [connection, refetchExamsWrapper]);

    return (
      <Box
        display="flex"
        width="100%"
        flexDirection="column"
        sx={{
          backgroundColor:
            theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
          borderRadius: "4px",
          p: "8px",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="12px"
        >
          <Typography
            alignContent="center"
            fontWeight={600}
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            maxWidth="100%"
          >
            {t("Exams")}
          </Typography>

          <Button
            onClick={() => setExpanded((prev) => !prev)}
            size="small"
            sx={{
              color: "inherit",
              textTransform: "none",
              textDecoration: "underline",
            }}
          >
            {expanded ? t("Collapse") : t("Expand")}
          </Button>
        </Box>

        <Collapse in={expanded} timeout="auto">
          {isLoading && <TableSkeleton numOfRows={LOADING_NUM_OF_ROWS} />}

          {hasExams && (
            <Box display="flex" flexDirection="column" mt="8px" gap="12px">
              <Grid container item xs={12}>
                <Grid item xs={1.5} />
                <Grid item xs={10.5}>
                  <Grid container item xs={12}>
                    <Grid item xs={10}>
                      <TableHeader<ExtendedExamType>
                        headCells={headCells}
                        order={order}
                        orderBy={orderByOptions?.[0] ?? "completionDate"}
                        onRequestSort={handleRequestSort}
                        rowCount={examsOnCurrentPage.length}
                        numSelected={0}
                      />
                    </Grid>
                    <Grid item xs={2} />
                  </Grid>
                </Grid>
              </Grid>
              {examsOnCurrentPage?.map((exam) => (
                <TodayExamCard
                  key={exam.id}
                  exam={exam}
                  handleEditClick={handleEditClick}
                />
              ))}
              <Box mt="12px">
                <CustomPagination
                  page={page}
                  count={getPageCount(sortedExams?.length, MAX_NUM_OF_ROWS)}
                  onChange={onPaginationChange}
                />
              </Box>
            </Box>
          )}

          {/* Error messages */}
          {!hasNoExams && hasNoSearchResults && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              my="60px"
            >
              <Typography textAlign="center" fontWeight={600}>
                {t("No Exams Found.")}
              </Typography>
              <Typography>
                {t("Change your search term to see more results.")}
              </Typography>
            </Box>
          )}
          {hasNoExams && (
            <Box display="flex" justifyContent="center" my="60px">
              <Typography textAlign="center">
                {t("No Exams Scheduled For Today.")}
              </Typography>
            </Box>
          )}
        </Collapse>

        {/* Modal to select a patient before adding a new exam */}
        <SelectPatientModal
          open={openPatientModal}
          onClose={() => setOpenPatientModal(false)}
          onConfirm={() => {
            setOpenPatientModal(false);
            setOpenAddExamSlider(true);
          }}
          modalDescription={`${t("Select the patient you want to add an exam to")}:`}
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
        />

        {/* Slider to add a new exam */}
        <ExamSlider
          key="add-exam-slider"
          title={`${t("Add Exam")} / ${selectedPatient.displayName}`}
          isOpen={openAddExamSlider}
          isLoading={isLoading}
          onClose={() => setOpenAddExamSlider(false)}
          onConfirm={onConfirmAddExam}
        />
        {/* Slider to edit an exam */}
        <ExamSlider
          key="edit-exam-slider"
          title={`${t("Edit Exam")} / ${selectedPatient.displayName}`}
          isOpen={openEditExamSlider}
          isLoading={isLoading}
          onClose={() => setOpenEditExamSlider(false)}
          onConfirm={onConfirmEditExam}
        />

        <LiveUpdateModal
          isLoadingSignalR={isLoadingSignalR}
          connection={connection}
          refetchExams={refetchExamsWrapper}
        />
      </Box>
    );
  }
);

TodayExams.displayName = "TodayExams";

export default TodayExams;
